import React from 'react'
import moment from 'moment-timezone'

import { AddToCalendarButton, Button, Card, Contact, Heading, Link } from 'core'
import { CalendarIcon, ClockIcon } from 'icons'
import { SUBTYPE_LABELS, absoluteLinkTo, isRegistered, linkTo } from 'event-taxonomy'
import { formatTimeRange, formatWebinarDate } from 'helpers'

import css from './style.scss'

/**
 * One row in the upcoming-events list. Webinars show their start time and
 * facilitator; session sales appear only under the "Registration Deadlines"
 * filter and show the deadline instead.
 *
 * Title and date/time used to sit in fixed-width grid columns, which got
 * painfully cramped in the "laptop" width range (too wide to stack, too
 * narrow to breathe). Stacking the date/time as a full-width row under the
 * title sidesteps that squeeze at every width instead of patching one
 * breakpoint. "View Details" lives in that same bottom rail, pushed to the
 * far right, so it lands in the same place regardless of title length.
 *
 * "Add to Calendar" only applies to webinars — a registration deadline
 * isn't a meeting, so there's nothing to put on a calendar.
 */
const EventListItem = ({ event, now }) => {
  const { target } = event
  const isDeadline = event.type === 'session_sale'
  const date = isDeadline ? target.open_registration_end_at : target.start_at
  const isLive = !isDeadline &&
    moment(now).isBetween(target.start_at, target.end_at, null, '[]')

  return (
    <Card>
      <p className={css.eventEyebrow}>{SUBTYPE_LABELS[target.type]}</p>

      <Heading size='h4' as='h2' weight='semibold'>{target.title}</Heading>

      {target.facilitator && <Contact size='xs' user={target.facilitator} />}

      <div className={css.eventMeta}>
        {isDeadline && <p className={css.deadlineEyebrow}>Registration closes</p>}
        <span className={css.eventMetaItem}>
          <CalendarIcon className={css.eventMetaIcon} aria-hidden='true' />
          {formatWebinarDate(date)}
        </span>
        {!isDeadline && (
          <span className={css.eventMetaItem}>
            <ClockIcon className={css.eventMetaIcon} aria-hidden='true' />
            {formatTimeRange(target.start_at, target.end_at)}
          </span>
        )}
        {isLive && <span className={css.liveIndicator}>Live Now</span>}
        {isRegistered(event) && <span className={css.registered}>* Registered</span>}
        {!isDeadline && (
          <AddToCalendarButton
            name={target.title}
            description={`View details: ${absoluteLinkTo(event)}`}
            startAt={target.start_at}
            endAt={target.end_at}
          />
        )}

        {/* Link auto-detects absolute URLs and renders an <a target> for
            external events; target is ignored for internal react-router
            links, so it's always safe to pass. */}
        <Link to={linkTo(event)} target='_blank' className={css.eventAction}>
          <Button size='tertiary' noMargin>View Details</Button>
        </Link>
      </div>
    </Card>
  )
}

EventListItem.propTypes = {
  event: React.PropTypes.shape({
    id: React.PropTypes.number,
    type: React.PropTypes.oneOf(['webinar', 'session_sale']).isRequired,
    target: React.PropTypes.object.isRequired
  }).isRequired,
  now: React.PropTypes.any
}

export default EventListItem
